<script setup lang="ts">
import { computed, ref } from 'vue'

import { CreateCustomCohortFlow } from '../../flows/CreateCustomCohortFlow'

import { useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { Button, Modal, MultiSelect, type SelectOption } from '@/shared/ui'

const open = defineModel<boolean>({ default: false })
const emit = defineEmits<{ created: [] }>()
const { t } = useI18n()
const workspace = useWorkspace()
const selected = ref<string[]>([])
const submitting = ref(false)
const failed = ref(false)

const eventOptions = computed<SelectOption<string>[]>(() => {
  const raw = workspace.getAttribute('mmp_events')?.value
  const values = raw && typeof raw === 'object' ? Object.values(raw).flat() : []
  return [...new Set(values.filter((value): value is string => typeof value === 'string'))].map(
    (value) => ({ value, label: value.replaceAll('_', ' ') }),
  )
})

async function submit(): Promise<void> {
  const workspaceId = workspace.workspaceId.value
  if (workspaceId === undefined || selected.value.length === 0) return
  failed.value = false
  const flow = new CreateCustomCohortFlow(undefined, {
    start: () => {
      submitting.value = true
    },
    finish: () => {
      submitting.value = false
    },
  })
  try {
    await flow.execute(Number(workspaceId), selected.value)
    selected.value = []
    open.value = false
    emit('created')
  } catch {
    failed.value = true
  }
}
</script>

<template>
  <Modal v-model="open" size="md" :title="t('commerceAnalytics.appAcquisition.custom.title')">
    <MultiSelect
      v-model="selected"
      :options="eventOptions"
      :label="t('commerceAnalytics.appAcquisition.custom.metric')"
      :placeholder="t('commerceAnalytics.appAcquisition.custom.placeholder')"
    />
    <p v-if="failed" class="custom-cohort__error mt-sm text-danger" role="alert">
      {{ t('commerceAnalytics.appAcquisition.custom.error') }}
    </p>
    <template #footer>
      <Button variant="ghost" :disabled="submitting" @click="open = false">
        {{ t('commerceAnalytics.appAcquisition.custom.cancel') }}
      </Button>
      <Button :disabled="submitting || selected.length === 0" :loading="submitting" @click="submit">
        {{ t('commerceAnalytics.appAcquisition.custom.complete') }}
      </Button>
    </template>
  </Modal>
</template>
